import type { AuthResult, PublicUser } from "@vouch/shared";

// Trim + `||` (not `??`) so an empty/whitespace VITE_API_URL — e.g. the image
// was built without the GitHub Variable set — falls back instead of becoming a
// relative same-origin URL, which would silently POST to the web's nginx and
// return 405. In a production build with no VITE_API_URL, shout loudly: the
// localhost fallback can't work in the browser, so this is a build misconfig.
const API_URL = (import.meta.env.VITE_API_URL as string | undefined)?.trim() || "http://localhost:8080";
if (import.meta.env.PROD && !(import.meta.env.VITE_API_URL as string | undefined)?.trim()) {
  // eslint-disable-next-line no-console
  console.error(
    "VITE_API_URL was not set at build time — API calls will fail. Rebuild the vouch-web image with VITE_API_URL=https://api.vouch.xoredge.com.",
  );
}

const ACCESS_KEY = "vouch_access";

let accessToken: string | null = localStorage.getItem(ACCESS_KEY);
let refreshing: Promise<string | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
  if (token) localStorage.setItem(ACCESS_KEY, token);
  else localStorage.removeItem(ACCESS_KEY);
}

export function getAccessToken(): string | null {
  return accessToken;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly body?: unknown,
  ) {
    super(message);
  }
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  /** Skip the access token (used by public/auth endpoints). */
  anonymous?: boolean;
  signal?: AbortSignal;
  /** Internal: prevents infinite refresh recursion. */
  _retry?: boolean;
}

async function refreshAccessToken(): Promise<string | null> {
  // De-dupe concurrent refreshes.
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${API_URL}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      if (!res.ok) return null;
      const data = (await res.json()) as AuthResult;
      setAccessToken(data.accessToken);
      return data.accessToken;
    } catch {
      return null;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {};
  // Only advertise a JSON body when we're actually sending one — otherwise
  // Fastify rejects bodyless requests (e.g. DELETE) with "Body cannot be empty
  // when content-type is set to 'application/json'".
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (!opts.anonymous && accessToken) headers.authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    credentials: "include",
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !opts.anonymous && !opts._retry) {
    const fresh = await refreshAccessToken();
    if (fresh) return api<T>(path, { ...opts, _retry: true });
  }

  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const message =
      (data as { message?: string } | null)?.message ?? `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }
  return data as T;
}

export async function apiFetch(path: string, opts: RequestOptions = {}): Promise<Response> {
  const headers: Record<string, string> = {};
  if (opts.body !== undefined) headers["content-type"] = "application/json";
  if (!opts.anonymous && accessToken) headers.authorization = `Bearer ${accessToken}`;

  const res = await fetch(`${API_URL}${path}`, {
    method: opts.method ?? "GET",
    headers,
    credentials: "include",
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
    signal: opts.signal,
  });

  if (res.status === 401 && !opts.anonymous && !opts._retry) {
    const fresh = await refreshAccessToken();
    if (fresh) return apiFetch(path, { ...opts, _retry: true });
  }

  if (!res.ok) {
    const text = await res.text();
    let data: unknown = null;
    if (text) {
      try {
        data = JSON.parse(text) as unknown;
      } catch {
        data = text;
      }
    }
    const message =
      typeof data === "object" && data !== null && "message" in data
        ? String((data as { message?: unknown }).message)
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, message, data);
  }

  return res;
}

// ─── Typed auth helpers ─────────────────────────────────────────────────────

export const authApi = {
  register: (body: { name: string; username: string; email: string; password: string }) =>
    api<AuthResult>("/auth/register", { method: "POST", body, anonymous: true }),
  login: (body: { username?: string; email?: string; password: string }) =>
    api<AuthResult>("/auth/login", { method: "POST", body, anonymous: true }),
  logout: () => api<{ ok: true }>("/auth/logout", { method: "POST", body: {} }),
  me: () => api<{ user: PublicUser }>("/auth/me"),
  verifyEmail: (token: string) =>
    api<{ ok: true }>("/auth/verify-email", { method: "POST", body: { token }, anonymous: true }),
  forgotPassword: (email: string) =>
    api<{ ok: true }>("/auth/forgot-password", {
      method: "POST",
      body: { email },
      anonymous: true,
    }),
  resetPassword: (token: string, password: string) =>
    api<{ ok: true }>("/auth/reset-password", {
      method: "POST",
      body: { token, password },
      anonymous: true,
    }),
};

export { API_URL };
